import AbstractFilterView, { type AbstractFilterViewArgs } from '@UI/Helpers/Filters/AbstractFilterView';

export type AuditTrailFilterViewArgs = AbstractFilterViewArgs & {
	readonly occurredFrom?: Date | null;
	readonly occurredTo?: Date | null;
	readonly actorUserId?: number | null;
	readonly action?: readonly string[];
	readonly outcome?: readonly string[];
	readonly resourceType?: readonly string[];
	readonly resourceId?: number | null;
	readonly beneficiaryId?: number | null;
	readonly facilityId?: number | null;
};

/**
 * Every field is a server filter — the journal is paginated server-side and a page never carries
 * enough rows to filter usefully in memory. `occurredBetween` is the one field that does not pass
 * through as-is: it is composed from two independent bounds, emitted only once both are chosen, and
 * always in chronological order regardless of click order.
 */
class AuditTrailFilterView extends AbstractFilterView<AuditTrailFilterViewArgs> {
	readonly occurredFrom: Date | null;
	readonly occurredTo: Date | null;
	readonly actorUserId: number | null;
	readonly action: readonly string[];
	readonly outcome: readonly string[];
	readonly resourceType: readonly string[];
	readonly resourceId: number | null;
	readonly beneficiaryId: number | null;
	readonly facilityId: number | null;

	constructor(args: AuditTrailFilterViewArgs) {
		super(args);

		this.occurredFrom = args.occurredFrom ?? null;
		this.occurredTo = args.occurredTo ?? null;
		this.actorUserId = args.actorUserId ?? null;
		this.action = args.action ?? [];
		this.outcome = args.outcome ?? [];
		this.resourceType = args.resourceType ?? [];
		this.resourceId = args.resourceId ?? null;
		this.beneficiaryId = args.beneficiaryId ?? null;
		this.facilityId = args.facilityId ?? null;

		Object.freeze(this);
	}

	static createDefault(): AuditTrailFilterView {
		return new AuditTrailFilterView({});
	}

	get hasCompletePeriod(): boolean {
		return this.occurredFrom !== null && this.occurredTo !== null;
	}

	/** Number of criteria that narrow the results; sorting never counts. */
	get activeFilterCount(): number {
		return [
			this.occurredFrom !== null || this.occurredTo !== null,
			this.actorUserId !== null,
			this.action.length > 0,
			this.outcome.length > 0,
			this.resourceType.length > 0,
			this.resourceId !== null,
			this.beneficiaryId !== null,
			this.facilityId !== null,
		].filter(Boolean).length;
	}

	override get queryParams(): URLSearchParams {
		const params = super.queryParams;

		if (this.actorUserId !== null) {
			params.set('actorUserId', String(this.actorUserId));
		}

		for (const action of this.action) {
			params.append('action', action);
		}

		for (const outcome of this.outcome) {
			params.append('outcome', outcome);
		}

		for (const resourceType of this.resourceType) {
			params.append('resourceType', resourceType);
		}

		if (this.resourceId !== null) {
			params.set('resourceId', String(this.resourceId));
		}

		if (this.beneficiaryId !== null) {
			params.set('beneficiaryId', String(this.beneficiaryId));
		}

		if (this.facilityId !== null) {
			params.set('facilityId', String(this.facilityId));
		}

		// Only emitted once both bounds are chosen: mid-selection (one bound, one `null`) would send
		// a `null` to `@IsDate({ each: true })` and turn the server's `400` into a flash of error on
		// every first click of the period picker. The server does not verify ordering either, so a
		// reversed pick is re-sorted here rather than silently returning zero rows.
		if (this.occurredFrom !== null && this.occurredTo !== null) {
			const [lowerBound, upperBound] =
				this.occurredFrom.getTime() <= this.occurredTo.getTime()
					? [this.occurredFrom, this.occurredTo]
					: [this.occurredTo, this.occurredFrom];

			params.append('occurredBetween', lowerBound.toISOString());
			params.append('occurredBetween', upperBound.toISOString());
		}

		return params;
	}

	setPeriod(occurredFrom: Date | null, occurredTo: Date | null): this {
		return this.cloneWith({ occurredFrom, occurredTo, page: 0 });
	}

	setActorUserId(actorUserId: number | null): this {
		return this.cloneWith({ actorUserId, page: 0 });
	}

	setAction(action: readonly string[]): this {
		return this.cloneWith({ action, page: 0 });
	}

	setOutcome(outcome: readonly string[]): this {
		return this.cloneWith({ outcome, page: 0 });
	}

	setResourceType(resourceType: readonly string[]): this {
		return this.cloneWith({ resourceType, page: 0 });
	}

	setResourceId(resourceId: number | null): this {
		return this.cloneWith({ resourceId, page: 0 });
	}

	setBeneficiaryId(beneficiaryId: number | null): this {
		return this.cloneWith({ beneficiaryId, page: 0 });
	}

	setFacilityId(facilityId: number | null): this {
		return this.cloneWith({ facilityId, page: 0 });
	}
}

export default AuditTrailFilterView;
